import { API } from "@/lib/api";

const BASE = `${API}/admin/interview`;

async function call(method, path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return {};
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(typeof data.detail === "string" ? data.detail : "Request failed");
    err.status = res.status;
    throw err;
  }
  return data;
}

export const ivApi = {
  extractTopics: (topic, notes) => call("POST", "/topics/extract", { topic, notes }),
  brief: (guest_name, topic, notes) => call("POST", "/brief", { guest_name, topic, notes }),
  coverage: (topics, transcript) => call("POST", "/topics/coverage", { topics, transcript }),
  listInterviews: () => call("GET", "/interviews"),
  getInterview: (id) => call("GET", `/interviews/${id}`),
  createInterview: (body) => call("POST", "/interviews", body),
  updateInterview: (id, body) => call("PUT", `/interviews/${id}`, body),
  deleteInterview: (id) => call("DELETE", `/interviews/${id}`),
};

export async function transcribeBlob(blob, prompt) {
  const fd = new FormData();
  fd.append("file", blob, "chunk.webm");
  fd.append("prompt", prompt || "");
  const res = await fetch(`${BASE}/transcribe`, { method: "POST", body: fd, credentials: "include" });
  if (!res.ok) throw new Error("Transcription failed");
  return (await res.json()).text;
}

export async function streamSuggestions(payload, onText, signal) {
  const res = await fetch(`${BASE}/suggest`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal,
  });
  if (!res.ok) throw new Error("Could not generate questions");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let full = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop();
    for (const ev of events) {
      const data = ev.replace(/^data: /, "");
      if (data === "[DONE]") return full;
      const msg = JSON.parse(data);
      if (msg.error) throw new Error(msg.error);
      full += msg.t;
      onText(full);
    }
  }
  return full;
}

const TYPES = ["Deeper", "Clarify", "Challenge", "Story", "Connect", "Pivot"];

export function parseQuestions(text) {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const [type, ...rest] = l.split("|");
      const raw = type.replace(/[^a-zA-Z]/g, "").toLowerCase();
      const t = raw.charAt(0).toUpperCase() + raw.slice(1);
      if (rest.length && TYPES.includes(t)) return { type: t, text: rest.join("|").trim() };
      return { type: "Deeper", text: l.replace(/^[-*\d.\s]+/, "") };
    })
    .filter((q) => q.text.length > 3);
}

const stamp = (ts, t0) => {
  const s = Math.max(0, Math.floor((ts - t0) / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

const list = (title, items) => (items?.length ? [`**${title}**`, ...items.map((x) => `- ${x}`), ""] : []);

const briefMd = (b) => [
  "## Prep brief", b.snapshot, "",
  ...list("Key facts", b.key_facts),
  ...list("Likely angles", (b.angles || []).map((a) => `${a.title}: ${a.why}`)),
  ...list("Opening questions", b.openers),
  ...list("Go deeper later", b.deeper),
  ...list("Watch outs", b.watch_outs),
];

export function interviewToMarkdown(iv) {
  const guest = iv.guest_name || "Guest";
  const segs = [...(iv.segments || [])].sort((a, b) => a.ts - b.ts);
  const t0 = segs[0]?.ts || 0;
  return [
    `# ${iv.topic || "Interview"}${iv.guest_name ? ` — ${iv.guest_name}` : ""}`,
    iv.created_at ? `_${new Date(iv.created_at).toLocaleString()}_` : "",
    "",
    ...(iv.brief ? briefMd(iv.brief) : []),
    "## Shortlisted questions",
    ...(iv.pinned?.length ? iv.pinned.map((p) => `- [${p.asked ? "x" : " "}] ${p.text}`) : ["_None_"]),
    "",
    "## Topic coverage",
    ...(iv.topics?.length ? iv.topics.map((t) => `- ${t.label} — ${t.status}`) : ["_None_"]),
    "",
    "## Transcript",
    ...segs.map((s) => `**[${stamp(s.ts, t0)}] ${s.speaker === "guest" ? guest : "Host"}:** ${s.text}`),
  ].join("\n");
}

export function downloadMarkdown(iv) {
  const blob = new Blob([interviewToMarkdown(iv)], { type: "text/markdown" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  const name = [iv.guest_name, iv.topic].filter(Boolean).join("-") || "interview";
  a.download = `${name.replace(/[^a-z0-9-]+/gi, "_").toLowerCase()}.md`;
  a.click();
  URL.revokeObjectURL(a.href);
}
